import { createSlice, createSelector, PayloadAction } from '@reduxjs/toolkit';
import type { SimpsonResult } from '@/utils/simpsonMethod/types';
import type { SpectralDopplerResult } from '@/models/spectralDoppler';
import type { RootState } from '../store';

export type MeasurementToolType =
  | 'length'
  | 'area'
  | 'simpson'
  | 'angle'
  | 'spectral-doppler-probe'
  | 'spectral-doppler-segment'
  | 'spectral-doppler-vti';

/** Spectral Doppler tools: usable only on a spectral Doppler image. */
export const isSpectralTool = (tool: MeasurementToolType): boolean =>
  tool.startsWith('spectral-doppler-');

/** Which tool families the selected image takes. */
export interface SelectedImageTools {
  /** A calibrated spectral Doppler region. */
  spectral: boolean;
  /** No spectral Doppler region, calibrated or not. */
  twoD: boolean;
}

/** Whether the image takes the tool. No image selected (null) gates nothing. */
export const canUseTool = (
  tool: MeasurementToolType,
  image: SelectedImageTools | null | undefined
): boolean => !image || (isSpectralTool(tool) ? image.spectral : image.twoD);

/** Hover text when the image can't take the tool: which image it needs. */
export function unavailableHintFor(
  tool: MeasurementToolType,
  image: SelectedImageTools
): string {
  if (!isSpectralTool(tool)) return 'Select a 2D image to measure';
  return image.twoD
    ? 'Select a spectral Doppler image to measure'
    : 'This Doppler image has no cm/s calibration, so it can’t be measured';
}

export type SimpsonMeasurementType = 'EDV' | 'ESV' | 'LAV';

export interface ActiveMeasurement {
  canonicalMeasurement: string;
  measurementLabel: string;
  toolType: MeasurementToolType;
  unit: string;
  precision?: number;
  simpsonMeasurementType?: SimpsonMeasurementType;
}

export type MeasurementStatus = 'idle' | 'awaiting' | 'completed';

export type WorldPoint = [number, number, number];

export type AnnotationType = 'POINT' | 'LINE' | 'ANGLE' | 'FREEHAND';

export interface PendingAnnotationMeta {
  annotationUID: string;
  type: AnnotationType;
  dicomUuid: string;
  frameNumber: number;
  points: WorldPoint[];
}

/** Dispatched when a hydrated annotation is resized/moved on the viewport */
export interface HydratedMeasurementUpdate {
  annotationUID: string;
  canonicalMeasurement: string;
  value: number;
  unit: string;
  points: WorldPoint[];
  /** Annotation type for the persisted row — 'FREEHAND' for Simpson contours.
   *  Omitted for hydrated lines, which keep their existing/default type. */
  type?: AnnotationType;
  /** DICOM the contour belongs to (user-drawn Simpson has no AI source). */
  dicomUuid?: string;
  /** 1-based frame number for Cornerstone imageId matching. */
  frameNumber?: number;
}

export interface MeasurementSessionState {
  activeMeasurement: ActiveMeasurement | null;
  pendingValue: number | null;
  pendingAnnotationMeta: PendingAnnotationMeta | null;
  status: MeasurementStatus;
  simpsonResults: Record<string, SimpsonResult>;
  /** One VTI/probe/segment annotation → one bundle of derived Doppler values. */
  spectralDopplerResults: Record<string, SpectralDopplerResult>;
  selectedAnnotationUID: string | null;
  deletedAnnotationUID: string | null;
  /** Runtime map of Cornerstone annotationUID → canonicalMeasurement */
  annotationToMeasurement: Record<string, string>;
  /** Pending update from resizing a hydrated annotation */
  hydratedMeasurementUpdate: HydratedMeasurementUpdate | null;
  /** Tool families the selected viewport's image takes; null = no
   *  image selected, so nothing is gated. */
  selectedImageTools: SelectedImageTools | null;
}

const initialState: MeasurementSessionState = {
  activeMeasurement: null,
  pendingValue: null,
  pendingAnnotationMeta: null,
  status: 'idle',
  simpsonResults: {},
  spectralDopplerResults: {},
  selectedAnnotationUID: null,
  deletedAnnotationUID: null,
  annotationToMeasurement: {},
  hydratedMeasurementUpdate: null,
  selectedImageTools: null,
};

export const measurementSessionSlice = createSlice({
  name: 'measurementSession',
  initialState,
  reducers: {
    startMeasurement: (state, action: PayloadAction<ActiveMeasurement>) => {
      state.activeMeasurement = action.payload;
      state.pendingValue = null;
      state.status = 'awaiting';
      // Arming clears any selected trace (AP-764: armed and selected are
      // separate emphases; a new draw should not compete with a highlighted
      // annotation). The useSharedToolState selection effect reacts to this
      // and restores the deselected annotation's base style in the viewer.
      state.selectedAnnotationUID = null;
    },
    completeMeasurement: (
      state,
      action: PayloadAction<{
        value: number;
        annotationMeta?: PendingAnnotationMeta;
      }>
    ) => {
      state.pendingValue = action.payload.value;
      state.pendingAnnotationMeta = action.payload.annotationMeta ?? null;
      state.status = 'completed';
      // A finished draw selects its annotation (AP-764 §3.10): leaving
      // 'awaiting' disarms in this same action, so I7 (armed/selected mutually
      // exclusive) holds — the emphasis hands over from armed to selected.
      if (action.payload.annotationMeta?.annotationUID) {
        state.selectedAnnotationUID =
          action.payload.annotationMeta.annotationUID;
      }
    },
    addSimpsonResult: (state, action: PayloadAction<SimpsonResult>) => {
      const result = action.payload;
      if (result.annotationUID) {
        state.simpsonResults[result.annotationUID] = result;
      }
    },
    removeSimpsonResult: (state, action: PayloadAction<string>) => {
      delete state.simpsonResults[action.payload];
    },
    // Full session reset — dispatched on study navigation (I7 turn-off rules):
    // no emphasis, mapping, undo history, or tool result may outlive its study.
    // Keeps the viewer-owned image flag; the viewer only re-sends it on change.
    resetSession: (state) => ({
      ...initialState,
      selectedImageTools: state.selectedImageTools,
    }),
    setSelectedImageTools: (
      state,
      action: PayloadAction<SelectedImageTools | null>
    ) => {
      state.selectedImageTools = action.payload;
    },
    clearAllSimpsonResults: (state) => {
      state.simpsonResults = {};
    },
    clearSimpsonResultsForDicom: (state, action: PayloadAction<string>) => {
      const dicomUuid = action.payload;
      Object.keys(state.simpsonResults)
        .filter((key) => state.simpsonResults[key].dicomUuid === dicomUuid)
        .forEach((key) => delete state.simpsonResults[key]);
    },
    addSpectralDopplerResult: (
      state,
      action: PayloadAction<SpectralDopplerResult>
    ) => {
      const result = action.payload;
      state.spectralDopplerResults[result.annotationUID] = result;
    },
    removeSpectralDopplerResult: (state, action: PayloadAction<string>) => {
      delete state.spectralDopplerResults[action.payload];
    },
    clearSpectralDopplerResultsForDicom: (
      state,
      action: PayloadAction<string>
    ) => {
      const dicomUuid = action.payload;
      Object.keys(state.spectralDopplerResults)
        .filter(
          (key) => state.spectralDopplerResults[key].dicomUuid === dicomUuid
        )
        .forEach((key) => delete state.spectralDopplerResults[key]);
    },
    cancelMeasurement: (state) => {
      state.activeMeasurement = null;
      state.pendingValue = null;
      state.status = 'idle';
    },
    clearMeasurement: (state) => {
      state.activeMeasurement = null;
      state.pendingValue = null;
      state.pendingAnnotationMeta = null;
      state.status = 'idle';
    },
    selectAnnotation: (state, action: PayloadAction<string | null>) => {
      state.selectedAnnotationUID = action.payload;
    },
    mapAnnotationToMeasurement: (
      state,
      action: PayloadAction<{
        annotationUID: string;
        canonicalMeasurement: string;
      }>
    ) => {
      state.annotationToMeasurement[action.payload.annotationUID] =
        action.payload.canonicalMeasurement;
    },
    updateHydratedMeasurement: (
      state,
      action: PayloadAction<HydratedMeasurementUpdate>
    ) => {
      state.hydratedMeasurementUpdate = action.payload;
    },
    clearHydratedMeasurementUpdate: (state) => {
      state.hydratedMeasurementUpdate = null;
    },
    deleteAnnotation: (state, action: PayloadAction<string>) => {
      state.deletedAnnotationUID = action.payload;
    },
    /** Drop an annotation → measurement mapping (AP-773 undo of a draw). */
    unmapAnnotation: (state, action: PayloadAction<string>) => {
      delete state.annotationToMeasurement[action.payload];
    },
    clearDeletedAnnotation: (state) => {
      // Also clean up the mapping + any spectral Doppler or Simpson result
      // for the deleted annotation — otherwise useSimpsonOverlay keeps
      // rendering the disks for a contour that's no longer in Cornerstone.
      if (state.deletedAnnotationUID) {
        delete state.annotationToMeasurement[state.deletedAnnotationUID];
        delete state.spectralDopplerResults[state.deletedAnnotationUID];
        delete state.simpsonResults[state.deletedAnnotationUID];
      }
      state.deletedAnnotationUID = null;
    },
  },
});

export const {
  startMeasurement,
  completeMeasurement,
  addSimpsonResult,
  removeSimpsonResult,
  resetSession,
  clearAllSimpsonResults,
  clearSimpsonResultsForDicom,
  addSpectralDopplerResult,
  removeSpectralDopplerResult,
  clearSpectralDopplerResultsForDicom,
  cancelMeasurement,
  clearMeasurement,
  selectAnnotation,
  mapAnnotationToMeasurement,
  unmapAnnotation,
  updateHydratedMeasurement,
  clearHydratedMeasurementUpdate,
  deleteAnnotation,
  clearDeletedAnnotation,
  setSelectedImageTools,
} = measurementSessionSlice.actions;

// Selectors
export const selectActiveMeasurement = (state: RootState) =>
  state.measurementSession.activeMeasurement;
export const selectMeasurementStatus = (state: RootState) =>
  state.measurementSession.status;
export const selectPendingValue = (state: RootState) =>
  state.measurementSession.pendingValue;
export const selectSimpsonResults = (state: RootState) =>
  state.measurementSession.simpsonResults;
export const selectAllSimpsonResults = createSelector(
  [selectSimpsonResults],
  (simpsonResults) => Object.values(simpsonResults)
);
export const selectSpectralDopplerResults = (state: RootState) =>
  state.measurementSession.spectralDopplerResults;
export const selectAllSpectralDopplerResults = createSelector(
  [selectSpectralDopplerResults],
  (spectralDopplerResults) => Object.values(spectralDopplerResults)
);
export const selectMeasurementSession = (state: RootState) =>
  state.measurementSession;
export const selectPendingAnnotationMeta = (state: RootState) =>
  state.measurementSession.pendingAnnotationMeta;
export const selectDeletedAnnotationUID = (state: RootState) =>
  state.measurementSession.deletedAnnotationUID;
export const selectAnnotationToMeasurement = (state: RootState) =>
  state.measurementSession.annotationToMeasurement;
export const selectSelectedAnnotationUID = (state: RootState) =>
  state.measurementSession.selectedAnnotationUID;
export const selectSelectedImageTools = (state: RootState) =>
  state.measurementSession.selectedImageTools;
export const selectHydratedMeasurementUpdate = (state: RootState) =>
  state.measurementSession.hydratedMeasurementUpdate;

export default measurementSessionSlice.reducer;
